import { BRAND } from '@bb/shared/core';
import { useState } from 'react';
import type { MeDto } from '@bb/shared/core';
import { api, ApiError } from '../api';
import { Chips, Seg } from '../components';
import { platform } from '../platform';

type Feeding = 'breast' | 'formula' | 'mixed';
type Sex = 'female' | 'male';

function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Код из ссылки-приглашения: «https://t.me/bot?start=inv_XXX», «inv_XXX» или сам код. */
export function inviteToken(text: string): string | null {
  const s = text.trim();
  const m = s.match(/inv_([A-Za-z0-9_-]{8,64})/);
  if (m) return m[1]!;
  return /^[A-Za-z0-9_-]{12,64}$/.test(s) ? s : null;
}

export function Onboarding({ me, onDone, inviteError }: { me: MeDto; onDone: () => Promise<void>; inviteError?: string | null }) {
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [sex, setSex] = useState<Sex | null>(null);
  const [feeding, setFeeding] = useState<Feeding | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [joinError, setJoinError] = useState<string | null>(inviteError ?? null);

  async function join() {
    const token = inviteToken(code);
    if (!token) return setJoinError('Не похоже на ссылку-приглашение. Скопируйте её целиком.');
    setBusy(true);
    setJoinError(null);
    try {
      await api.acceptInvite(token);
      platform.haptic('success');
      await onDone();
    } catch (e) {
      setJoinError(e instanceof ApiError ? e.message : 'Приглашение не сработало');
      setBusy(false);
    }
  }

  const today = todayLocal();
  const step1Error = !name.trim() ? 'Как зовут малыша?' : !birthDate ? 'Укажите дату рождения' : birthDate > today ? 'Дата рождения не может быть в будущем' : null;

  async function finish() {
    setBusy(true);
    setError(null);
    try {
      await api.createFamily({
        child: { name: name.trim(), birthDate, sex, birthWeightG: null, feeding },
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Moscow',
        displayName: me.user.firstName,
      });
      platform.haptic('success');
      await onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не получилось сохранить');
      setBusy(false);
    }
  }

  if (step === 0) {
    return (
      <main className="app center">
        <div className="avatar" style={{ width: 88, height: 88, fontSize: 36 }} aria-hidden="true">
          ✦
        </div>
        <h1 className="h1">{BRAND.name}</h1>
        <p className="muted">{BRAND.slogan}</p>
        <p className="muted" style={{ maxWidth: 320 }}>
          Кормления, сон и подгузники в пару касаний. Вся семья видит одно и то же, а бот напомнит о кормлении.
        </p>
        <button type="button" className="btn btn-primary btn-block" style={{ maxWidth: 360 }} onClick={() => setStep(1)}>
          Начать
        </button>
        <form
          className="card stack"
          style={{ maxWidth: 360, width: '100%', textAlign: 'left' }}
          onSubmit={(e) => {
            e.preventDefault();
            void join();
          }}
        >
          <span className="section-title">Вас пригласили?</span>
          <span className="tiny muted">Откройте ссылку из приглашения — дневник подключится сам. Или вставьте её сюда:</span>
          {joinError && <p className="error" data-testid="invite-error">{joinError}</p>}
          <input className="input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="https://t.me/…?start=inv_…" aria-label="Ссылка-приглашение" autoComplete="off" />
          <button type="submit" className="btn btn-soft btn-block" disabled={busy || !code.trim()}>
            Присоединиться к семье
          </button>
        </form>
      </main>
    );
  }

  if (step === 1) {
    return (
      <main className="app stack">
        <p className="section-title">Шаг 1 из 2</p>
        <h1 className="h1">Знакомимся</h1>
        <div className="field">
          <label htmlFor="ob-name">Имя малыша</label>
          <input id="ob-name" className="input" value={name} maxLength={60} autoComplete="off" onChange={(e) => setName(e.target.value)} placeholder="Например, Миша" />
        </div>
        <div className="field">
          <label htmlFor="ob-birth">Дата рождения</label>
          <input id="ob-birth" className="input" type="date" max={today} value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
        </div>
        <div className="field">
          <span className="label">Пол (необязательно)</span>
          <Seg<Sex | 'none'>
            label="Пол"
            value={sex ?? 'none'}
            options={[
              { value: 'female', label: 'Девочка' },
              { value: 'male', label: 'Мальчик' },
              { value: 'none', label: 'Не указывать' },
            ]}
            onChange={(v) => setSex(v === 'none' ? null : v)}
          />
        </div>
        {step1Error && (name || birthDate) && <p className="error">{step1Error}</p>}
        <button type="button" className="btn btn-primary btn-block" disabled={!!step1Error} onClick={() => setStep(2)}>
          Дальше
        </button>
      </main>
    );
  }

  return (
    <main className="app stack">
      <p className="section-title">Шаг 2 из 2</p>
      <h1 className="h1">Чем кормите?</h1>
      <p className="muted">Так мы покажем нужные кнопки первыми. Поменять можно в любой момент.</p>
      <Chips<Feeding>
        label="Кормление"
        value={feeding}
        options={[
          { value: 'breast', label: 'Грудью' },
          { value: 'formula', label: 'Смесью' },
          { value: 'mixed', label: 'Смешанное' },
        ]}
        onChange={setFeeding}
      />
      {error && <p className="error">{error}</p>}
      <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => void finish()}>
        {busy ? 'Сохраняем…' : 'Готово'}
      </button>
      <button type="button" className="btn btn-secondary btn-block" disabled={busy} onClick={() => setStep(1)}>
        Назад
      </button>
    </main>
  );
}
